import type { SupabaseClient } from '@supabase/supabase-js';
import { YYYY_MM_DD_REGEX } from '@/utils/date/calendarDate';
import { getNowInTimezone, getOrgTimezone } from '@/utils/cron/invoiceCronHelpers';

/**
 * First class date for an Ongoing Class (#4724).
 *
 * The date lives on `classes.first_class_date` and is CASCADED into every weekly
 * `class_schedules.effective_date` by the class API on save. Every "does this
 * class meet on date X" surface already honours that window, so nothing here is
 * read by a meeting surface — these helpers only decide what the API stamps and
 * whether a given date is before the first class.
 *
 * All dates are 'YYYY-MM-DD' strings compared lexically. Every `today` /
 * `gymToday` here is the GYM-local date (getNowInTimezone(getOrgTimezone(..)));
 * the class routes resolve it once per save via gymLocalToday below.
 */

export function normalizeFirstClassDate(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return YYYY_MM_DD_REGEX.test(trimmed) ? trimmed : null;
}

export interface ResolveOngoingScheduleStartInput {
  /** classes.first_class_date — wins when set. */
  firstClassDate: string | null | undefined;
  /** class_sections.session_start_date the form sent back for this section (ongoing classes only). */
  sectionSessionStartDate: string | null | undefined;
  /** Gym-local today, 'YYYY-MM-DD'. */
  today: string;
}

/**
 * What an ongoing class's WEEKLY schedule rows get as `effective_date`.
 * The first class date wins over the section date; with neither, today
 * (the pre-#4724 behaviour). A past first class date is stamped as-is —
 * it is history, not an error.
 */
export function resolveOngoingScheduleStart(input: ResolveOngoingScheduleStartInput): string {
  const firstClassDate = normalizeFirstClassDate(input.firstClassDate);
  if (firstClassDate) return firstClassDate;
  const sectionStart = normalizeFirstClassDate(input.sectionSessionStartDate);
  if (sectionStart) return sectionStart;
  return input.today;
}

/**
 * What an ongoing class's SECTION row gets as `session_start_date` on save.
 * The section date is a second, narrower bound inside the proration meeting
 * counter and the drop-in gates, so it must never sit LATER than the first
 * class date — otherwise the calendar (which reads the cascaded weekly row)
 * and billing would disagree. Earlier-or-equal section dates are kept; with
 * no first class date the section date passes through untouched.
 */
export function resolveOngoingSectionStart(input: {
  firstClassDate: string | null | undefined;
  sectionSessionStartDate: string | null | undefined;
}): string | null {
  const first = normalizeFirstClassDate(input.firstClassDate);
  const section = normalizeFirstClassDate(input.sectionSessionStartDate);
  if (!first) return section;
  if (!section) return null;
  return section > first ? first : section;
}

/** Gym-local today for the org, 'YYYY-MM-DD'. */
export async function gymLocalToday(supabase: SupabaseClient, organizationId: string): Promise<string> {
  const tz = await getOrgTimezone(supabase, organizationId);
  return getNowInTimezone(tz).dateStr;
}

/**
 * Today at the CLASS's location, 'YYYY-MM-DD' — the clock the public catalog
 * and detail page use for "Starts <date>" (schedule-location timezone). The
 * checkout's "is this class still ahead" must agree with what the family just
 * read, so it asks the same clock; a two-timezone gym otherwise disagrees for
 * a few hours around midnight on the eve of the first class (PR 1 carry-over).
 * With no location, or a location without a timezone, falls back to the org
 * clock (`gymLocalToday`). Billing MONTHS stay on the org clock — that is the
 * clock the monthly run keeps.
 */
export async function classLocalToday(
  supabase: SupabaseClient,
  organizationId: string,
  locationId: string | null | undefined
): Promise<string> {
  if (locationId) {
    const { data } = await supabase
      .from('gymlocations')
      .select('timezone')
      .eq('id', locationId)
      .eq('organization_id', organizationId)
      .maybeSingle();
    const tz = typeof data?.timezone === 'string' && data.timezone ? data.timezone : null;
    if (tz) return getNowInTimezone(tz).dateStr;
  }
  return gymLocalToday(supabase, organizationId);
}

/**
 * A one-off Added Class Date (a slot whose effective_date === end_date) that
 * falls before the first class date. The form refuses these; the API refuses
 * them too so a raw or stale PUT cannot persist a meeting the catalog says
 * has not started. Returns the offending date or null.
 */
export function oneOffSlotBeforeFirstClass(
  slots: Array<{ effective_date?: unknown; end_date?: unknown }> | null | undefined,
  firstClassDate: string | null | undefined
): string | null {
  const first = normalizeFirstClassDate(firstClassDate);
  if (!first || !slots) return null;
  for (const slot of slots) {
    const start = normalizeFirstClassDate(slot.effective_date);
    const end = normalizeFirstClassDate(slot.end_date);
    if (start && end && start === end && start < first) return start;
  }
  return null;
}

/** True when `dateStr` falls before the class's first class date (no date ⇒ false). */
export function isBeforeFirstClass(
  dateStr: string,
  firstClassDate: string | null | undefined
): boolean {
  const first = normalizeFirstClassDate(firstClassDate);
  if (!first) return false;
  return dateStr < first;
}

/**
 * The date the catalog should say the class "Starts" — only while the first
 * class is still ahead of gym-local today. After that, nothing (R4/R10).
 */
export function upcomingFirstClassDate(
  firstClassDate: string | null | undefined,
  gymToday: string
): string | null {
  const first = normalizeFirstClassDate(firstClassDate);
  if (!first) return null;
  return first > gymToday ? first : null;
}

/**
 * Column value the class API writes. Session Programs never carry it — the
 * CHECK constraint (`is_session_based IS NOT TRUE`) refuses it — so flipping a
 * dated ongoing class to Session Program clears it instead of failing the
 * save. Open Gym is cleared here in application code only; the constraint
 * does not mention is_open_gym. While the feature flag is off nothing is
 * written, so production rows stay NULL whatever a client sends.
 */
export function firstClassDateForWrite(input: {
  firstClassDate: unknown;
  isSessionBased: boolean;
  isOpenGym: boolean;
  featureEnabled: boolean;
}): string | null {
  if (!input.featureEnabled || input.isSessionBased || input.isOpenGym) return null;
  return normalizeFirstClassDate(input.firstClassDate);
}
